import { useRef, useState, useEffect } from 'react';
import {
    DropDownList,
    DropDownListCloseEvent,
    DropDownListFilterChangeEvent,
    DropDownListPageChangeEvent
} from '@progress/kendo-react-dropdowns';
import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import { Hint, Label, Error } from '@progress/kendo-react-labels';
import { filterBy } from '@progress/kendo-data-query';

export const FormDropDownWithSearch = (fieldRenderProps: FieldRenderProps) => {
    const {
        validationMessage,
        touched,
        label,
        id,
        valid,
        disabled,
        hint,
        wrapperStyle,
        className,
        required,
        data,
        textField = 'name',
        ...others
    } = fieldRenderProps;

    const editorRef = useRef(null);

    const [currentData, setCurrentData] = useState(data);

    useEffect(() => {
        setCurrentData(data);
    }, [data]);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint;
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    const [skip, setSkip] = useState(0);
    const pageSize = 30;
    const virtualScrollThreshold = 100;

    const onPageChange = (event: DropDownListPageChangeEvent) => {
        setSkip(event.page.skip);
    };

    const onFilterChange = (event: DropDownListFilterChangeEvent) => {
        setCurrentData(filterBy(data.slice(), event.filter));
        setSkip(0);
    };
    
    const onClose = (event: DropDownListCloseEvent) => {
        setSkip(0);
        others.onClose?.(event);
    };

    const handleChange = (event: any) => {
        event.value = others.valueMap ? others.valueMap(event.value) : event.value;
        others.onChange(event);
    };

    const valueItem =
        others.valueMap && others.value !== null && others.value !== undefined
            ? data.find((x: any) => others.valueMap(x) === others.value)
            : others.value;
    
    const isVirtual = currentData.length >= virtualScrollThreshold;
    const listData = isVirtual ? currentData.slice(skip, skip + pageSize) : currentData;

    return (
        <FieldWrapper className={className} style={wrapperStyle}>
            {!others.hideLabel && (
                <Label
                    id={labelId}
                    editorRef={editorRef}
                    editorId={id}
                    editorValid={valid}
                    editorDisabled={disabled}
                    className={`${required ? 'required' : null}`}
                >
                    {label}
                </Label>
            )}
            <div className={'k-form-field-wrap'}>
                <DropDownList
                    ariaLabelledBy={labelId}
                    ariaDescribedBy={`${hintId} ${errorId}`}
                    ref={editorRef}
                    valid={valid}
                    id={id}
                    disabled={disabled}

                    filterable={true}
                    onFilterChange={onFilterChange}

                    data={listData}
                    textField={textField}

                    {...others}
                    value={valueItem}
                    onChange={handleChange}
                    virtual={isVirtual ? {
                        total: currentData.length,
                        pageSize: pageSize,
                        skip: skip
                    } : undefined}
                    onPageChange={isVirtual ? onPageChange : undefined}
                    onClose={onClose}
                />
                {showHint && <Hint id={hintId}>{hint}</Hint>}
                {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
            </div>
        </FieldWrapper>
    );
};
